import React, { useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import CancelBtn from '@components/profile/profileOverlay/overlays/common/cancelBtn';
import RemoveBtn from '@components/profile/profileOverlay/overlays/common/removeBtn';
import { ProjectStateType } from '@components/profile/profileOverlay/overlays/project/index';
import { useSaveProfileProjects } from '@repository/profiles/useProfileProject';
import { ProfileProjectSaveVO } from '@domain/rq/profile/ProfileProjectListSaveRq';
import { SourcePage } from '@domain/rq/DataLog';
import ProfileProjectsVO, { ProfileProjectVO } from '@domain/vo/profile/ProfileProjectVO';
import classnames from 'classnames/bind';
import styles from './project.module.scss';
const cx = classnames.bind(styles);

interface ProjectListProps {
  projects?: ProfileProjectVO[];
  setDeleteList: (deleteList: (prev: ProfileProjectVO[]) => ProfileProjectVO[]) => void;
}

const ProjectList = ({ projects, setDeleteList }: ProjectListProps) => {
  const listEls = projects?.map((project, index) => {
    const { title, startDate, endDate } = project;
    const handleClick = () => {
      setDeleteList((prev) => [...prev, project]);
    };

    return (
      <li key={index} className={cx('listItem')}>
        <div className={cx('btnList', 'delete')}>
          <div className={cx('contentsArea')}>
            <span className={cx('listText')}>{title}</span>
            <span className={cx('listDesc')}>
              {startDate} ~ {endDate}
            </span>
          </div>

          <RemoveBtn onClick={handleClick} />
        </div>
      </li>
    );
  });

  return <ul className={cx('list')}>{listEls}</ul>;
};

const EmptyList = () => {
  return <div className={cx('emptyList')}>입력하신 프로젝트 경험 정보가 없어요.</div>;
};

interface ProjectDeleteListProps {
  refetch: () => Promise<QueryObserverResult>;
  profileProjects?: ProfileProjectsVO;
  setProjectState: (projectState: ProjectStateType) => void;
  openExitModal: () => void;
}

const ProjectDeleteList = ({ refetch, profileProjects, setProjectState, openExitModal }: ProjectDeleteListProps) => {
  const Toast = useToast();
  const { query } = useRouter();
  const [deleteList, setDeleteList] = useState<ProfileProjectVO[]>([]);
  const isUpdated = deleteList.length !== 0;

  const { mutate: saveProfileProject } = useSaveProfileProjects(() => {
    refetch().then(() => {
      Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
      setProjectState('LIST');
    });
  });

  const handlePrev = () => {
    if (isUpdated) {
      openExitModal();
      return;
    }

    setProjectState('LIST');
  };

  const handleSubmit = () => {
    saveProfileProject({
      projects: getList()?.map((project) => ProfileProjectSaveVO.convertProfileProjectSaveVO(project)) || [],
      sourcePage: query.source_page as SourcePage,
    });
  };

  const getList = () =>
    profileProjects?.projects.filter((project) => !deleteList.find((deleteProject) => deleteProject === project));

  const getListEl = () => {
    if (deleteList.length === profileProjects?.projects.length) return <EmptyList />;
    return <ProjectList projects={getList()} setDeleteList={setDeleteList} />;
  };

  return (
    <>
      <div className={cx('scrollArea')}>
        <div className={cx('scrollInner')}>
          <div className={cx('scrollWrap')}>
            <div className={cx('listArea')}>
              {getListEl()}
              <div className={cx('cancelArea')}>
                <CancelBtn onClick={() => setProjectState('LIST')} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cx('btnArea')}>
        <a className={cx('btn', 'cancel')} role="button" onClick={handlePrev}>
          이전
        </a>
        <a className={cx('btn', 'confirm')} role="button" onClick={handleSubmit}>
          저장
        </a>
      </div>
    </>
  );
};

export default ProjectDeleteList;
